import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ECaseSnackBarService, ECaseUtils, ExplorerService} from 'synto-common';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DocumentItem} from '../document-list.models';
import {
  ARCHIVE_OUTCOME_UNKNOWN,
  archiveOutcome,
  ArchiveOutcome,
  isUnknownRequestOutcome,
  OutcomeMessage
} from '../utils/document-approval.utils';

export interface DocumentArchiveDialogData {
  row: DocumentItem;
  screenName: string;
}

@Component({
  selector: 'app-document-archive-dialog',
  templateUrl: './document-archive-dialog.component.html',
  styleUrls: ['./document-archive-dialog.component.scss']
})
export class DocumentArchiveDialogComponent implements OnDestroy {

  submitting = false;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<DocumentArchiveDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentArchiveDialogData,
    private translate: TranslateService,
    private explorerService: ExplorerService,
    private eCaseSnackBarService: ECaseSnackBarService
  ) {
  }

  onConfirm(): void {
    if (!this.data?.row || this.submitting) {
      return;
    }
    const edmRepositoryDocumentId = this.data.row?.edmRepositoryDocumentId;
    if (!edmRepositoryDocumentId) {
      this.eCaseSnackBarService.show(
        'failure',
        ECaseUtils.getTranslatedValueFromKey(this.translate, 'ecase.explorer.documentList.archive.documentIdMissing')
      );
      return;
    }
    this.submitting = true;
    this.dialogRef.disableClose = true;
    this.explorerService.archiveDocument(edmRepositoryDocumentId, this.data.screenName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => this.handleResponse(response),
        error: (error: any) => this.handleRequestError(error)
      });
  }

  onClose(): void {
    if (!this.submitting) {
      this.dialogRef.close(false);
    }
  }

  private handleResponse(response: any): void {
    const reported = response?.approvalWorkflow;
    if (typeof reported !== 'string') {
      this.show(ARCHIVE_OUTCOME_UNKNOWN);
      this.close();
      return;
    }
    this.finish(archiveOutcome(reported));
  }

  private handleRequestError(error: any): void {
    const reported = error?.error?.approvalWorkflow;
    if (reported) {
      this.finish(archiveOutcome(reported));
      return;
    }
    if (isUnknownRequestOutcome(error)) {
      this.show(ARCHIVE_OUTCOME_UNKNOWN);
      this.close();
      return;
    }
    const errorMessage = error?.errorMessage ||
      error?.error?.errorMessage ||
      error?.error?.message ||
      error?.message ||
      'ecase.explorer.documentList.archive.error';
    this.show({status: 'failure', key: errorMessage});
    this.close();
  }

  private finish(outcome: ArchiveOutcome): void {
    this.show(outcome);
    if (outcome.retryable) {
      this.submitting = false;
      this.dialogRef.disableClose = false;
      return;
    }
    this.close();
  }

  private show(message: OutcomeMessage): void {
    this.eCaseSnackBarService.show(
      message.status,
      ECaseUtils.getTranslatedValueFromKey(this.translate, message.key)
    );
  }

  private close(): void {
    this.submitting = false;
    this.dialogRef.disableClose = false;
    this.dialogRef.close(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
